import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DealService, CargoDealDetail } from '../../../services/api/deal';
import { CargoRequestService, CargoRequest } from '../../../services/api/cargo-request';
import { CustomsCalculationsService } from '../../../services/api/customs-calculations';
import { FreightForwarderBidService, FreightForwarderBidDto } from '../../../services/api/freight-forwarder-bid';
import { LanguageService } from '../../../services/i18n/language';
import { DocNumberPipe } from '../../../services/request-number';
import { isDeliveredByCodes } from '../../../services/deal-stage';
import { competitionGain } from '../../../services/bid-savings';
import { paymentSummary, CurrencyTotals } from '../../../services/deal-payments';
import { HomeCurrencyService, fmtMoney, sumIn, othersText, totalsText, mergeTotals } from '../../../services/home-currency';

interface DealRow {
  id: number;
  createdAt: string;
  route: string;
  forwarder: string;
  stage: string;
  /** Когда экспедитор последний раз ставил статус. */
  lastStatusAt: string | null;
  handedOver: boolean;
  delivered: boolean;
  weightKg: number;
  /** Счета и оплаты по валютам. */
  totals: CurrencyTotals[];
  cargoRequestId: number;
  bidId: number | null;
}

/** Этапы, после которых груз считается переданным (как в счётчике на странице сделки). */
const HANDOVER_CODES = ['CARGO_PICKUP_DONE', 'CARGO_AT_FORWARDER_WAREHOUSE', 'IN_TRANSIT'];

/** Статус, не обновлявшийся дольше этого, — повод спросить экспедитора. */
const STALE_DAYS = 2;

/**
 * Сводка грузовладельца: запросы, где пора выбрать экспедитора, сделки в пути, что оплатить,
 * поставки в обработке и итоги. Всё считается из тех же запросов, что и разделы кабинета.
 */
@Component({
  selector: 'app-co-overview',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe],
  templateUrl: './co-overview.component.html',
  // Та же вёрстка, что у «Сводки» экспедитора
  styleUrl: '../../freight-forwarder/ff-overview/ff-overview.component.scss'
})
export class CoOverviewComponent implements OnInit, OnDestroy {
  loading = true;
  deals: DealRow[] = [];
  /** Запросы, по которым пришли ставки и ещё не выбран экспедитор. */
  awaitingChoice: CargoRequest[] = [];
  suppliesInWork: number | null = null;
  /** Ставки по запросам сделок (для «Выгоды от конкурса»); null — ещё грузятся. */
  private dealBids: { bidId: number; bids: FreightForwarderBidDto[] }[] | null = null;

  private subscriptions = new Subscription();

  // ─── Деньги — в валюте страны компании; счета в других валютах не пересчитываются ───

  /** Валюта сводок — по стране компании (₸ для Казахстана, € для Европы…). */
  currency = 'KZT';

  /** Сумма в валюте компании. */
  home(v: number): string {
    return fmtMoney(v, this.currency, this.languageService.getCurrentLanguageValue());
  }

  /** Итог по набору сделок: в валюте компании, а если в ней ничего нет — в других валютах. */
  homeSum(rows: { totals: CurrencyTotals[] }[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    const v = sumIn(rows, this.currency, field);
    return v > 0 ? this.home(v) : this.dealTotals(mergeTotals(rows), field);
  }

  /** Суммы в других валютах — «+ 3 000 USD»; пусто, если их нет или итог уже показан в них. */
  homeOthers(rows: { totals: CurrencyTotals[] }[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    if (!(sumIn(rows, this.currency, field) > 0)) return '';
    return othersText(rows, this.currency, field, this.languageService.getCurrentLanguageValue());
  }

  /** Поле по сделке (или нескольким) во всех валютах, своя первой; пусто — «0» в своей. */
  dealTotals(totals: CurrencyTotals[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    return totalsText(totals, this.currency, field, this.languageService.getCurrentLanguageValue()) || this.home(0);
  }

  constructor(
    private dealService: DealService,
    private cargoRequestService: CargoRequestService,
    private calculationsService: CustomsCalculationsService,
    private bidService: FreightForwarderBidService,
    private homeCurrencyService: HomeCurrencyService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
    this.subscriptions.add(this.homeCurrencyService.currency$.subscribe(c => {
      this.currency = c;
      this.cdr.markForCheck();
    }));

    const pending$ = this.cargoRequestService.searchCargoRequests({ status: 'PENDING' }, 0, 50).pipe(catchError(() => of(null)));
    const supplies$ = this.calculationsService.searchCalculations({ status: 'IN_PROCESSING' }, 0, 1).pipe(catchError(() => of(null)));
    const deals$ = this.dealService.getMyDealsPaginated(0, 50).pipe(catchError(() => of(null)));

    this.subscriptions.add(
      forkJoin([pending$, supplies$, deals$]).subscribe(([pending, supplies, deals]) => {
        this.awaitingChoice = (pending?.content ?? []).filter(r => !r.isDraft && !r.hasDeal && (r.bidsCount ?? 0) > 0);
        this.suppliesInWork = supplies?.totalElements ?? null;
        const list = deals?.content ?? [];
        if (!list.length) {
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }
        // Маршрут, этап и счета есть только в карточке сделки
        this.subscriptions.add(
          forkJoin(list.map(d => this.dealService.getDealDetail(d.id).pipe(catchError(() => of(null))))).subscribe(details => {
            this.deals = details.filter((d): d is CargoDealDetail => !!d).map(d => this.toRow(d));
            this.loading = false;
            this.cdr.markForCheck();
            this.loadSavings();
          })
        );
      })
    );
  }

  /** Ставки по запросу каждой сделки — для «Выгоды от конкурса». */
  private loadSavings(): void {
    const withBid = this.deals.filter(d => d.bidId != null && d.cargoRequestId);
    if (!withBid.length) {
      this.dealBids = [];
      return;
    }
    this.subscriptions.add(
      forkJoin(withBid.map(d => this.bidService.getBidsByCargoRequestId(d.cargoRequestId).pipe(catchError(() => of(null))))).subscribe(lists => {
        this.dealBids = lists.map((bids, i) => ({ bidId: withBid[i].bidId!, bids: bids ?? [] }));
        this.cdr.markForCheck();
      })
    );
  }

  /** Выгода от конкурса: сумма по сделкам, где выбранная ставка дешевле средней по остальным; null — ещё грузится. */
  get competitionGainTotal(): number | null {
    if (!this.dealBids) return null;
    return this.dealBids.reduce((s, x) => s + (competitionGain(x.bids, x.bidId, this.currency)?.gain ?? 0), 0);
  }

  /** Сколько всего ставок получено по запросам сделок — показываем, когда выгоды пока нет. */
  get bidsReceived(): number {
    return (this.dealBids ?? []).reduce((s, x) => s + x.bids.length, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private toRow(d: CargoDealDetail): DealRow {
    const lang = this.languageService.getCurrentLanguageValue();
    const statuses = (d.activities ?? []).filter(a => a.activityType?.category === 'STATUS');
    const last = [...statuses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    const codes = statuses.map(a => a.activityType?.code);
    const r = d.cargoRequest;
    const pay = paymentSummary(d.activities);
    return {
      id: d.id,
      createdAt: d.createdAt,
      route: `${r?.fromCity ?? ''} → ${r?.toCity ?? ''}`,
      forwarder: d.freightForwarder?.companyName ?? '',
      stage: last ? ((lang === 'en' ? last.activityType.nameEn : last.activityType.nameRu) || '—') : '—',
      lastStatusAt: last?.createdAt ?? null,
      handedOver: codes.some(c => HANDOVER_CODES.includes(c)),
      delivered: isDeliveredByCodes(codes),
      weightKg: (r?.products ?? []).reduce((sum, p) => sum + (p.totalWeightKg || 0), 0),
      totals: pay.totals,
      cargoRequestId: r?.id ?? 0,
      bidId: d.bid?.id ?? null,
    };
  }

  // ─── Плитки ───

  /** Сделки в работе — пока груз не доставлен. */
  get activeDeals(): DealRow[] {
    return this.deals.filter(d => !d.delivered);
  }

  get bidsToReview(): number {
    return this.awaitingChoice.reduce((s, r) => s + (r.bidsCount ?? 0), 0);
  }

  get debtTotal(): number {
    return sumIn(this.deals, this.currency, 'debt');
  }

  // ─── Итоги ───

  get deliveredCount(): number {
    return this.deals.filter(d => d.delivered).length;
  }

  get inTransitCount(): number {
    return this.deals.filter(d => d.handedOver && !d.delivered).length;
  }

  get preparingCount(): number {
    return this.deals.filter(d => !d.handedOver && !d.delivered).length;
  }

  get deliveredWeightText(): string {
    return this.number(this.deals.filter(d => d.delivered).reduce((s, d) => s + d.weightKg, 0)) + ' ' + this.t('ffSearch.kg', 'кг');
  }

  get invoicedTotal(): number {
    return sumIn(this.deals, this.currency, 'invoiced');
  }

  get paidTotal(): number {
    return sumIn(this.deals, this.currency, 'paid');
  }

  // ─── Требует внимания ───

  get withDebt(): DealRow[] {
    return this.deals.filter(d => d.totals.some(t => t.debt > 0));
  }

  /** Экспедитор давно не обновлял статус недоставленной сделки. */
  get stale(): DealRow[] {
    const limit = Date.now() - STALE_DAYS * 86400000;
    return this.activeDeals.filter(d => !d.lastStatusAt || new Date(d.lastStatusAt).getTime() < limit);
  }

  /** Давно без статуса — сначала самые старые, не больше пяти в списке. */
  get staleShown(): DealRow[] {
    const at = (d: DealRow) => (d.lastStatusAt ? new Date(d.lastStatusAt).getTime() : 0);
    return [...this.stale].sort((x, y) => at(x) - at(y)).slice(0, 5);
  }

  /** Оплачено больше выставленного — зачтётся в следующий счёт. */
  get withOverpay(): DealRow[] {
    return this.deals.filter(d => d.totals.some(t => t.overpaid > 0));
  }

  get overpaidTotal(): number {
    return sumIn(this.deals, this.currency, 'overpaid');
  }

  get hasAttention(): boolean {
    return this.awaitingChoice.length > 0 || this.withDebt.length > 0 || this.withOverpay.length > 0 || this.stale.length > 0;
  }

  daysSince(iso: string | null): number {
    return iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0;
  }

  get latestDeals(): DealRow[] {
    return [...this.deals].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  }

  dealTone(d: DealRow): string {
    return d.delivered ? 'green' : d.handedOver ? 'blue' : '';
  }

  bidsLabel(n: number): string {
    if (this.languageService.getCurrentLanguageValue() !== 'ru') return `${n} ${this.t('coDash.bidsShort', 'bids')}`;
    const m10 = n % 10, m100 = n % 100;
    const word = m10 === 1 && m100 !== 11 ? 'ставка' : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? 'ставки' : 'ставок';
    return `${n} ${word}`;
  }

  kzt(v: number): string {
    return this.number(Math.round(v)) + ' ₸';
  }

  private number(n: number): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n || 0);
  }
}
